/**
 * Serialize answer POSTs for one mutation hook instance.
 *
 * Same answersVersion in flight or pending → join that promise.
 * Different version while busy → keep only the latest pending (drop obsolete).
 * After the current POST finishes, run that pending once (mutex — no parallel POSTs).
 */

export type AnswersSubmitInFlight = {
  answersVersion: number
  promise: Promise<void>
}

type PendingAnswersSubmit = {
  answersVersion: number
  work: () => Promise<void>
  promise: Promise<void>
  resolve: () => void
  reject: (reason?: unknown) => void
}

/** Mutable queue held in a ref by the submit mutation hook. */
export type AnswersSubmitQueue = {
  inFlight: AnswersSubmitInFlight | null
  pending: PendingAnswersSubmit | null
}

/** Empty queue for `useRef(createAnswersSubmitQueue())`. */
export function createAnswersSubmitQueue(): AnswersSubmitQueue {
  return { inFlight: null, pending: null }
}

function createPending(
  answersVersion: number,
  work: () => Promise<void>
): PendingAnswersSubmit {
  let resolve!: () => void
  let reject!: (reason?: unknown) => void
  const promise = new Promise<void>((res, rej) => {
    resolve = res
    reject = rej
  })
  return { answersVersion, work, promise, resolve, reject }
}

/** Start `work` as the sole in-flight submit; drain pending when it settles. */
function startInFlight(
  queueRef: { current: AnswersSubmitQueue },
  answersVersion: number,
  work: () => Promise<void>
): Promise<void> {
  const entry: AnswersSubmitInFlight = {
    answersVersion,
    promise: Promise.resolve()
  }
  entry.promise = (async () => {
    try {
      await work()
    } finally {
      const queue = queueRef.current
      if (queue.inFlight === entry) {
        queue.inFlight = null
      }
      drainPending(queueRef)
    }
  })()
  queueRef.current.inFlight = entry
  return entry.promise
}

/** Run the coalesced pending submit, if any and nothing is in flight. */
function drainPending(queueRef: { current: AnswersSubmitQueue }): void {
  const queue = queueRef.current
  const pending = queue.pending
  if (!pending || queue.inFlight != null) return

  queue.pending = null
  startInFlight(queueRef, pending.answersVersion, pending.work).then(
    pending.resolve,
    pending.reject
  )
}

/**
 * Run work under the submit queue: dedupe same version, coalesce newer versions.
 */
export async function enqueueAnswersSubmit(
  queueRef: { current: AnswersSubmitQueue },
  answersVersion: number,
  work: () => Promise<void>
): Promise<void> {
  const queue = queueRef.current
  const inFlight = queue.inFlight

  // Duplicate of the POST already running
  if (inFlight != null && inFlight.answersVersion === answersVersion) {
    return inFlight.promise
  }

  // Duplicate of the POST waiting to run
  if (queue.pending != null && queue.pending.answersVersion === answersVersion) {
    return queue.pending.promise
  }

  // Idle — run now
  if (inFlight == null) {
    return startInFlight(queueRef, answersVersion, work)
  }

  // Busy with another version — keep only this latest pending
  const previousPending = queue.pending
  const pending = createPending(answersVersion, work)
  queue.pending = pending
  if (previousPending != null) {
    // Superseded waiters follow the new pending outcome (their work is dropped)
    pending.promise.then(previousPending.resolve, previousPending.reject)
  }
  return pending.promise
}
